import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  ExportCertificatesPdfMutationRequest,
  ExportCertificatesPdfMutationResponse,
} from "../types/ExportCertificatesPdf"

type ExportCertificatesPdfClient = typeof client<
  ExportCertificatesPdfMutationResponse,
  Error,
  ExportCertificatesPdfMutationRequest
>
type ExportCertificatesPdf = {
  data: ExportCertificatesPdfMutationResponse
  error: Error
  request: ExportCertificatesPdfMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<ExportCertificatesPdfClient>>
  client: {
    parameters: Partial<Parameters<ExportCertificatesPdfClient>[0]>
    return: Awaited<ReturnType<ExportCertificatesPdfClient>>
  }
}
/**
 * @summary Export certificates pdf
 * @link /v1/certificate/export-certificates-pdf
 */
export function useExportCertificatesPdf(
  options: {
    mutation?: UseMutationOptions<
      ExportCertificatesPdf["response"],
      ExportCertificatesPdf["error"],
      ExportCertificatesPdf["request"]
    >
    client?: ExportCertificatesPdf["client"]["parameters"]
  } = {}
): UseMutationResult<
  ExportCertificatesPdf["response"],
  ExportCertificatesPdf["error"],
  ExportCertificatesPdf["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    ExportCertificatesPdf["response"],
    ExportCertificatesPdf["error"],
    ExportCertificatesPdf["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        ExportCertificatesPdf["data"],
        ExportCertificatesPdf["error"],
        ExportCertificatesPdf["request"]
      >({
        method: "post",
        url: `/v1/certificate/export-certificates-pdf`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
